import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Monitor, Save, ArrowRight, Terminal, Code } from 'lucide-react';
import Background from '../components/Background';

const KEYS = {
  startTime: 'af_startTime',
  duration:  'af_duration',
} as const;

export default function Setup() {
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration]   = useState(24);
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    const st  = localStorage.getItem(KEYS.startTime);
    const dur = localStorage.getItem(KEYS.duration);
    if (dur) setDuration(Number(dur));
    if (st) {
      const d = new Date(st);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setStartTime(local);
    }
  }, []);

  function handleSave() {
    if (!startTime) { alert('Please set a start time.'); return; }
    localStorage.setItem(KEYS.startTime, new Date(startTime).toISOString());
    localStorage.setItem(KEYS.duration, String(duration));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-black text-white overflow-hidden px-4 py-10">
      <Background />
      <div className="scanlines" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-20 w-full max-w-lg"
      >
        {/* Card */}
        <div className="relative p-8 border bg-black/80 backdrop-blur-xl overflow-hidden"
          style={{ borderColor: 'rgba(0,255,255,0.3)' }}>

          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-400" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-red-500" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-red-500" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-400" />

          <motion.div className="absolute -inset-[1px] bg-gradient-to-r from-cyan-500/10 via-transparent to-red-500/10 -z-10"
            animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 3, repeat: Infinity }} />

          {/* Header */}
          <div className="flex items-center gap-3 mb-6 pb-5" style={{ borderBottom: '1px solid rgba(0,255,255,0.12)' }}>
            <div className="w-10 h-10 flex items-center justify-center"
              style={{ background: 'rgba(0,255,255,0.1)', border: '1px solid rgba(0,255,255,0.3)' }}>
              <Monitor className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="font-mono text-lg font-bold tracking-widest uppercase"
                style={{ background: 'linear-gradient(90deg, #00ffff, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Timer Setup
              </h1>
              <p className="font-mono text-xs tracking-wider text-gray-500 mt-0.5">
                Configure the countdown display
              </p>
            </div>
          </div>

          {/* Fields */}
          <div className="flex flex-col gap-5 mb-6">
            <Field label="Start Time" hint="The countdown begins from this exact moment.">
              <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)}
                className={inputCls} style={{ colorScheme: 'dark' }} />
            </Field>

            <Field label="Duration (hours)">
              <input type="number" min={1} max={72} value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                className={`${inputCls} w-28`} />
            </Field>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mb-5">
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleSave}
              className="flex-1 flex items-center justify-center gap-2 py-3 font-mono text-sm font-bold tracking-widest uppercase"
              style={saved
                ? { background: 'rgba(0,180,60,0.2)', border: '1px solid rgba(0,220,80,0.4)', color: '#4ade80' }
                : { background: 'linear-gradient(90deg,rgba(0,255,255,0.15),rgba(59,130,246,0.15))', border: '1px solid rgba(0,255,255,0.35)', color: '#00ffff' }
              }>
              {saved ? <><span>✓</span> Saved</> : <><Save className="w-4 h-4" /> Save</>}
            </motion.button>

            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => navigate('/countdown')}
              className="flex-1 flex items-center justify-center gap-2 py-3 font-mono text-sm font-bold tracking-widest uppercase"
              style={{ background: 'rgba(255,0,68,0.1)', border: '1px solid rgba(255,0,68,0.3)', color: '#ff4466' }}>
              Display <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>

          {/* Tips */}
          <div className="flex flex-col gap-3">
            <div className="flex gap-3 p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Terminal className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-gray-500 leading-relaxed">
                Save, then open Display in a new tab and press <strong className="text-gray-400">F11</strong> for fullscreen.
              </p>
            </div>

            <div className="flex gap-3 p-3" style={{ background: 'rgba(0,255,255,0.03)', border: '1px solid rgba(0,255,255,0.08)' }}>
              <Code className="w-4 h-4 text-cyan-800 flex-shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-gray-600 leading-relaxed">
                Broadcast messages are managed in <span className="text-cyan-700">Countdown.tsx → BROADCAST_LINES</span>. Edit and push to Vercel — all screens update on next load.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-4">
          <button onClick={() => navigate('/')}
            className="font-mono text-xs text-gray-600 hover:text-cyan-400 tracking-widest uppercase transition-colors">
            ← Back to AlgoForge site
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const inputCls = [
  'w-full bg-black/60 border font-mono text-sm text-white',
  'px-3 py-2.5 outline-none transition-all',
  'border-cyan-500/20 focus:border-cyan-400/60',
  'focus:shadow-[0_0_0_1px_rgba(0,255,255,0.2)]',
  'placeholder:text-gray-700',
].join(' ');

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-xs tracking-[0.3em] uppercase text-gray-500">{label}</span>
      {children}
      {hint && <span className="font-mono text-xs text-gray-700 tracking-wide">{hint}</span>}
    </label>
  );
}
